import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lightning } from "@phosphor-icons/react";
import api from "../lib/api";
import { flag, fmtData, fmtEUR, countryName } from "../lib/format";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function Topup() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState({ esim: null, plans: [] });
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  useEffect(() => {
    api.get(`/me/esims/${id}/topup-plans`)
      .then(({ data }) => setData(data))
      .catch(() => nav("/dashboard"))
      .finally(() => setLoading(false));
  }, [id, nav]);

  const buy = async (planId) => {
    setPaying(planId);
    try {
      const { data: res } = await api.post("/checkout/session", {
        plan_id: planId,
        origin_url: window.location.origin,
        target_iccid: data.esim.iccid,
      });
      window.location.href = res.url;
    } catch (e) {
      alert(e.response?.data?.detail || t("common.error"));
      setPaying(null);
    }
  };

  if (loading) return (<div className="min-h-screen"><Header /><div className="max-w-3xl mx-auto px-6 py-24 text-ink-500">{t("common.loading")}</div></div>);
  if (!data.esim) return null;

  const e = data.esim;

  return (
    <div className="min-h-screen" data-testid="topup-page">
      <Header />
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-12">
        <Link to="/dashboard" className="btn-ghost text-sm !pl-2 mb-4 inline-flex"><ArrowLeft size={16} /> {t("common.back")}</Link>
        <div className="overline mb-2">{t("common.topup")}</div>
        <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tight">{t("common.topupTitle")}</h1>
        <p className="mt-3 text-ink-700">{t("common.topupSub")}</p>

        <div className="mt-8 card-soft p-6 flex items-center gap-4 max-w-xl">
          <span className="text-4xl">{flag(e.plan_snapshot.country_iso2)}</span>
          <div className="flex-1">
            <div className="font-display font-semibold text-lg tracking-tight">{e.plan_snapshot.name}</div>
            <div className="text-xs text-ink-500 font-mono">{e.iccid}</div>
          </div>
          <div className="text-right text-sm">
            <div className="overline text-[10px]">{t("dash.dataLeft")}</div>
            <div className="font-display font-bold">{fmtData(e.data_left_mb)}</div>
          </div>
        </div>

        <h2 className="mt-12 mb-4 font-display font-semibold text-2xl tracking-tight">
          <Lightning size={22} weight="fill" className="inline mb-1 text-coral" /> {countryName(e.plan_snapshot.country_iso2, i18n.language, e.plan_snapshot.country)}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.plans.map((p) => (
            <div key={p.id} className="card-soft p-6 flex flex-col" data-testid={`topup-plan-${p.id}`}>
              <div className="flex items-start justify-between mb-5">
                <div className="font-display font-semibold text-xl tracking-tight">{p.name}</div>
                <div className="font-display font-bold text-2xl tracking-tight">{fmtEUR(p.retail_price)}</div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-cobalt-50 rounded-2xl p-3">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-cobalt-600">{t("common.data")}</div>
                  <div className="font-display font-bold mt-0.5">{fmtData(p.data_mb)}</div>
                </div>
                <div className="bg-coral-soft/60 rounded-2xl p-3">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-coral-deep">{t("common.days")}</div>
                  <div className="font-display font-bold mt-0.5">{p.days}</div>
                </div>
              </div>
              <button onClick={() => buy(p.id)} disabled={paying === p.id} className="btn-primary mt-auto w-full" data-testid={`topup-buy-${p.id}`}>
                {paying === p.id ? t("checkout.processing") : <>{t("common.topup")} <ArrowRight size={16} weight="bold" /></>}
              </button>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
